import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useThemeClasses } from '../hooks/useThemeClasses';

const API = process.env.REACT_APP_BACKEND_URL;

export const PolicyPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const theme = useThemeClasses();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPolicy();
  }, [slug]);

  const loadPolicy = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/api/policies/${slug}`);
      
      if (response.data.success) {
        setPolicy(response.data.data);
      } else {
        setError('Policy not found');
      }
    } catch (error) {
      console.error('Failed to load policy:', error);
      setError('Failed to load policy. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.bgPrimary} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className={`mt-4 ${theme.textSecondary}`}>Loading policy...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${theme.bgPrimary} flex items-center justify-center`}>
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-6xl mb-4">📄</div>
          <h2 className={`text-2xl font-bold mb-4 ${theme.textPrimary}`}>Policy Not Found</h2>
          <p className={`mb-6 ${theme.textSecondary}`}>{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Homepage
          </button>
        </div>
      </div>
    );
  }

  if (!policy) return null;

  return (
    <div className={`min-h-screen ${theme.bgPrimary} py-12`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center ${theme.textSecondary} hover:${theme.textPrimary} mb-4 transition-colors`}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-2xl">📋</span>
            </div>
            <h1 className={`text-3xl md:text-4xl font-bold ${theme.textPrimary}`}>
              {policy.title}
            </h1>
          </div>
          
          {policy.updated_at && (
            <p className={`text-sm ${theme.textSecondary}`}>
              Last updated: {new Date(policy.updated_at).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          )}
        </div>

        {/* Content */}
        <div className={`${theme.card} rounded-lg shadow-lg p-8 md:p-12 border border-gray-200`}>
          <div 
            className={`prose prose-lg max-w-none ${theme.textPrimary} policy-content`}
            dangerouslySetInnerHTML={{ __html: policy.content }}
          />
        </div>

        {/* Contact Section */}
        <div className={`mt-8 p-6 ${theme.bgSecondary} rounded-lg border border-gray-200`}>
          <h3 className={`text-lg font-semibold mb-2 ${theme.textPrimary}`}>Questions?</h3>
          <p className={theme.textSecondary}>
            If you have any questions about this policy, please contact us at{' '}
            <a 
              href="mailto:timelrd@rogers.com" 
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              timelrd@rogers.com
            </a>
          </p>
        </div>
      </div>

      <style jsx>{`
        .policy-content h1 {
          font-size: 2rem;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }
        
        .policy-content h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        
        .policy-content h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
        }
        
        .policy-content p {
          margin-bottom: 1rem;
          line-height: 1.75;
        }
        
        .policy-content ul, .policy-content ol {
          margin-left: 1.5rem;
          margin-bottom: 1rem;
        }
        
        .policy-content li {
          margin-bottom: 0.5rem;
          line-height: 1.75;
        }
        
        .policy-content strong {
          font-weight: 600;
        }
        
        .policy-content a {
          color: #2563eb;
          text-decoration: underline;
        }
        
        .policy-content a:hover {
          color: #1d4ed8;
        }
      `}</style>
    </div>
  );
};
